/* eslint-disable class-methods-use-this */
import Vue from 'vue';
import { callCustomizedBridge } from '@mx/neo';
import { bridgeTool } from '@mx/pay-common';
import type { SelectBankAction } from './constant';
import { metricPoints } from './constant';
import type { PayStatusParams } from '@/common';

enum BridgeName {
  NotifyHybridProcessResult = 'pay.notifyHybridProcessResult',
  NeoNotifyHybridProcessResult = 'pay_notifyHybridProcessResult',
  SelectBank = 'pay.selectBank',
}
bridgeTool.installCustomizedBridgeApi(callCustomizedBridge);
const statOptions = {
  start: {
    metricKey: metricPoints.bridgeStart,
  },
  success: {
    metricKey: metricPoints.bridgeSuccess,
  },
  fail: {
    metricKey: metricPoints.bridgeFail,
  },
};

const bridgeToolInst = bridgeTool.createInstance({
  statOptions,
});

class Bridge {
  KNBP = KNB.getPromiseInstance();

  isDev = process.env.NODE_ENV === 'development';

  setTitle(title: string) {
    document.title = title;
  }

  // 设置返回按钮
  setLLButton(handle: () => void, content?: string) {
    let llButtonConfig = { handle } as KNBLLButtonConfig;
    if (!content) {
      llButtonConfig = { ...llButtonConfig, icon: 'H5_Back' };
    } else if (this.isBase64(content)) {
      llButtonConfig = { ...llButtonConfig, type: 'base64', icon: content };
    } else {
      llButtonConfig = { ...llButtonConfig, type: 'text', text: content };
    }
    KNB.ready(() => {
      KNB.setLLButton(llButtonConfig);
    });
  }

  setBounces(val: number) {
    KNB.ready(() => {
      KNB.setBouncesEnabled({
        enable: val, // 1表示允许反弹，0表示禁止。default: 1
      });
    });
  }

  // 打开新页面
  openPage(url: string, fail = () => {}) {
    if (!url) {
      return;
    }
    if (KNB.env.isNative) {
      KNB.ready(() => {
        KNB.openPage({
          url,
          query: {
            _knbopeninapp: 1,
          },
          fail,
        });
      });
    } else {
      window.location.href = url;
    }
  }

  closePage(): void {
    KNB.ready(() => {
      KNB.closePage();
    });
  }

  getStorage(storageKey: string): Promise<any> {
    if (this.isDev) {
      return new Promise((resolve) => {
        resolve({ value: 'test' });
      });
    }

    return this.KNBP.getStorage({ key: storageKey });
  }

  clearStorage(storageKey: string): Promise<any> {
    if (this.isDev) {
      return new Promise((resolve) => {
        resolve(true);
      });
    }

    return this.KNBP.clearStorage({ key: storageKey });
  }

  notifyNative(name: string, data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      KNB.use(name, {
        ...data,
        success: resolve,
        fail: reject,
      });
    });
  }

  /**
   * 9.8.0 以后通过此桥回调业务方，此桥回展示支付后结果页面
   * https://km.sankuai.com/page/1297735889
   * https://km.sankuai.com/page/1296744395
   * @param params
   * @returns
   */
  notifyHybridProcessResultAndClosePage(params: CallbackNativeRouterDataTyep) {
    // 奇葩点：两个桥入参数，参数层级不一样，据 pengchong02 反馈由于历史原因如此
    // 入参格式不同 KNB 桥需要 data 包装，neo 业务自建桥不需要
    return bridgeToolInst.call({
      name: BridgeName.NeoNotifyHybridProcessResult,
      customizedBridge: true,
      params: {
        action: params.action,
        data: {
          dest_cashier_type: params.dest_cashier_type,
          source_cashier_type: params.source_cashier_type || 'pay_defer_sign',
          status: params.status,
          promotion: params.promotion,
          pay_result_extra: params.pay_result_extra,
        },
      },
    });
  }

  /**
   * 9.8.0 以后通过此桥回调业务方，此桥回展示支付后结果页面
   * KNB 桥
   * https://km.sankuai.com/page/1297735889
   * https://km.sankuai.com/page/1296744395
   * @param params
   * @returns
   */
  notifyKNBHybridProcessResultAndClosePage(params: CallbackNativeRouterDataTyep) {
    // 奇葩点：两个桥入参数，参数层级不一样，据 pengchong02 反馈由于历史原因如此
    // 入参格式不同 KNB 桥需要 data 包装，neo 业务自建桥不需要
    return bridgeToolInst.call({
      name: BridgeName.NotifyHybridProcessResult,
      params: {
        action: params.action,
        data: {
          dest_cashier_type: params.dest_cashier_type,
          source_cashier_type: params.source_cashier_type || 'pay_defer_sign',
          status: params.status,
          promotion: params.promotion,
          pay_result_extra: params.pay_result_extra,
        },
      },
    });
  }

  useMtpayPayment(action: SelectBankAction, params: BridgeSelectBank.SelectBankParams) {
    return bridgeToolInst.call<PayStatusParams>({
      name: BridgeName.SelectBank,
      params: {
        action,
        data: params,
      },
    });
  }

  private isBase64(content: string): boolean {
    return content.indexOf('data:image') === 0;
  }

  subscribe(options: Omit<{ action: string; handle: (data?: any) => void }, 'success' | 'fail'>) {
    return bridgeToolInst.call<{ subId: string }>({
      name: 'subscribe',
      params: options,
    });
  }

  unsubscribe(options: Omit<{ subId?: string; action?: string }, 'success' | 'fail'>) {
    return bridgeToolInst.call<void>({
      name: 'unsubscribe',
      params: options,
    });
  }
}

export const bridge = new Bridge();

// extend Vue prototype
declare module 'vue/types/vue' {
  interface Vue {
    $bridge: typeof bridge;
  }
}

Vue.prototype.$bridge = bridge;
